import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import AddressbookPanelShell from "../../addressbook/panels/AddressbookPanelShell";
import CollapsibleCard from "../../../components/panels/CollapsibleCard";
import LinkedDocumentsCard from "../../../components/panels/LinkedDocumentsCard";
import { apiGetOutstandingOutreach } from "../../../graphQL/deal";
import { STAGE_LABEL } from "./DealListPanel";

const FieldRow = ({ label, value }) => (
  <Box sx={{ display: "flex", px: 1.25, py: 0.5, gap: 1 }}>
    <Typography sx={{ fontSize: 11.5, color: "#94A3B8", width: 110, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 11.5, color: "#1A1F36", minWidth: 0, overflowWrap: "anywhere" }}>
      {value || "—"}
    </Typography>
  </Box>
);

const OUTCOME_LABEL = {
  noResponse: "No response",
  interested: "Interested",
  declined: "Declined",
  infoRequested: "Info requested",
  tourScheduled: "Tour scheduled",
};

// Read-only outreach ledger: the deal's listings split into contacted (with
// last touch + outcome) and untouched, from outstandingOutreach.
const OutreachLedgerCard = ({ dealId }) => {
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!dealId) {
        setLedger(null);
        return;
      }
      setLoading(true);
      try {
        const data = await apiGetOutstandingOutreach(dealId);
        if (!cancelled) setLedger(data);
      } catch (err) {
        if (!cancelled) setLedger(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [dealId]);

  const contacted = ledger?.contacted || [];
  const untouched = ledger?.untouched || [];

  return (
    <CollapsibleCard title="Outreach" summary={ledger?.summary || ""} defaultExpanded>
      {loading ? (
        <Typography sx={{ px: 1.25, py: 1, fontSize: 11.5, color: "#94A3B8" }}>Loading…</Typography>
      ) : !ledger ? (
        <Typography sx={{ px: 1.25, py: 1, fontSize: 11.5, color: "#94A3B8" }}>
          No outreach recorded yet.
        </Typography>
      ) : (
        <>
          {ledger.summary ? (
            <Typography sx={{ px: 1.25, pt: 0.75, fontSize: 11.5, color: "#3949AB", fontWeight: 600 }}>
              {ledger.summary}
            </Typography>
          ) : null}
          {contacted.map((row) => (
            <Box
              key={`c-${row.propertyAssetId}`}
              sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.25, py: 0.6, borderBottom: "1px solid #F4F5FB" }}
            >
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: "#10B981", flexShrink: 0 }} />
              <Typography sx={{ flex: 1, minWidth: 0, fontSize: 11.5, color: "#1A1F36", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {row.propertyLabel}
              </Typography>
              <Typography sx={{ fontSize: 10.5, color: "#64748B", whiteSpace: "nowrap" }}>
                {[
                  OUTCOME_LABEL[row.lastOutcome] || row.lastOutcome,
                  row.lastTouchAt ? new Date(row.lastTouchAt).toLocaleDateString() : "",
                ]
                  .filter(Boolean)
                  .join(" • ")}
              </Typography>
            </Box>
          ))}
          {untouched.map((row) => (
            <Box
              key={`u-${row.propertyAssetId}`}
              sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.25, py: 0.6, borderBottom: "1px solid #F4F5FB" }}
            >
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: "#CBD5E1", flexShrink: 0 }} />
              <Typography sx={{ flex: 1, minWidth: 0, fontSize: 11.5, color: "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {row.propertyLabel}
              </Typography>
              <Typography sx={{ fontSize: 10.5, color: "#94A3B8" }}>Not contacted</Typography>
            </Box>
          ))}
        </>
      )}
    </CollapsibleCard>
  );
};

const DealDetailPanel = ({ onClose, deal }) => (
  <AddressbookPanelShell
    onClose={onClose}
    titleContent={
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1A1F36" }}>
        {deal?.name || "Deal"}
      </Typography>
    }
  >
    {!deal ? (
      <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
        <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
          Select a deal to view details
        </Typography>
      </Box>
    ) : (
      <Box sx={{ p: 1, display: "flex", flexDirection: "column", gap: 1.25 }}>
        <CollapsibleCard title="Details" defaultExpanded>
          <Box sx={{ py: 0.5 }}>
            <FieldRow label="Stage" value={STAGE_LABEL[deal.stage] || deal.stage} />
            <FieldRow label="Type" value={deal.dealType} />
            <FieldRow label="Side" value={deal.side} />
            <FieldRow label="Status" value={deal.status} />
            <FieldRow
              label="Listings"
              value={
                Array.isArray(deal.propertyAssetIds)
                  ? String(deal.propertyAssetIds.length)
                  : "0"
              }
            />
            {deal.resolutionStatus === "pending" ? (
              <FieldRow label="Resolution" value="Pending review" />
            ) : null}
          </Box>
        </CollapsibleCard>
        <OutreachLedgerCard dealId={deal.id} />
        <LinkedDocumentsCard refCollection="deals" refId={deal.id} defaultExpanded />
      </Box>
    )}
  </AddressbookPanelShell>
);

export default DealDetailPanel;
